import { Heart } from 'lucide-react';
import { useCallback, useState, type MouseEvent } from 'react';
import { useIsFavorite, useToggleFavorite } from '@/hooks/useFavorites';
import { cn } from '@/lib/utils';

interface FavoriteButtonProps {
  slug: string;
  productName: string;
  /** Fotoğraf üzerinde mi duruyor? (cam zemin) */
  onImage?: boolean;
  size?: 'sm' | 'md';
  className?: string;
}

/**
 * Favori gerçekten çalışır ve backend'de saklanır: misafirde cihaz
 * kimliğine, üyede hesaba bağlıdır. Giriş yapıldığında cihaz listesi
 * hesaba taşınır (bkz. useMergeFavorites).
 */
export function FavoriteButton({
  slug,
  productName,
  onImage = false,
  size = 'md',
  className,
}: FavoriteButtonProps) {
  const isFavorite = useIsFavorite(slug);
  const toggle = useToggleFavorite();
  const [pulse, setPulse] = useState(false);

  const handleClick = useCallback(
    (event: MouseEvent) => {
      // Kart tıklaması ürün detayına gider; favori onu tetiklememeli.
      event.preventDefault();
      event.stopPropagation();

      setPulse(true);
      window.setTimeout(() => setPulse(false), 320);

      toggle.mutate({ slug, isFavorite, productName });
    },
    [slug, productName, isFavorite, toggle],
  );

  return (
    <button
      type="button"
      onClick={handleClick}
      aria-pressed={isFavorite}
      aria-label={
        isFavorite
          ? `${productName} favorilerden çıkar`
          : `${productName} favorilere ekle`
      }
      className={cn(
        'flex items-center justify-center rounded-pill transition-all duration-fast ease-flow',
        'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-leaf-700',
        'active:scale-90',
        size === 'sm' ? 'size-8' : 'size-10',
        onImage
          ? 'bg-white/80 backdrop-blur-md hover:bg-white shadow-soft'
          : 'hover:bg-surface-sunken',
        className,
      )}
    >
      <Heart
        aria-hidden
        className={cn(
          'transition-all duration-base ease-flow',
          size === 'sm' ? 'size-4' : 'size-[18px]',
          pulse && 'scale-125',
          isFavorite
            ? 'fill-blush-600 text-blush-600'
            : 'text-ink-muted hover:text-ink',
        )}
      />
    </button>
  );
}
